import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { createAIEvaluation, flattenExamQuestions, getAttemptScores, getEvaluationMark, getQuestionEntry } from '../utils/examScores';
import { gradeStarterSpreadsheet, hasDescriptiveResponse, hasStarterSpreadsheet } from '../utils/starterSpreadsheet';
import { spreadsheetToText, getWorkbookSheets } from '../components/editors/workbookModel';
import { buildEvaluationMaterial } from './evaluationMaterial';

export const EVALUATION_TIMEOUT_MS = 125000;
export const EVALUATION_BATCH_TIMEOUT_MS = 360000;

// A disconnected service must never leave an exam trapped on a marking screen.
export function withEvaluationTimeout(promise, timeoutMs = EVALUATION_TIMEOUT_MS, message = 'Evaluation took too long. Please retry the pending answers.') {
    let timer;
    return Promise.race([
        promise,
        new Promise((_, reject) => { timer = setTimeout(() => reject(Object.assign(new Error(message), {code: 'deadline-exceeded'})), timeoutMs); }),
    ]).finally(() => clearTimeout(timer));
}

const markValue = value => Math.max(0, Number(value) || 0);
const normalized = value => String(value ?? '').trim().toLowerCase();
const selected = value => value !== undefined && value !== null && String(value).trim() !== '';
const sameSelection = (a, b) => selected(a) && selected(b) && String(a) === String(b);
const hasDrawnAnswer = answer => {
    const word = typeof answer === 'string' ? answer : answer?.wordContent || '';
    return /<(?:path|line|rect|circle|ellipse|polygon|polyline|text|use|foreignObject|canvas)\b/i.test(word) ||
        getWorkbookSheets(answer?.excelData).some(sheet => sheet.data.drawings?.strokes?.length);
};

export function gradeObjectiveQuestion(question, answer) {
    const correct = question.correctAnswer;
    switch (question.type) {
        case 'mcq': {
            const expected = Array.isArray(correct) ? correct.map(String).sort() : [];
            const actual = Array.isArray(answer) ? answer.map(String).sort() : [];
            return expected.length > 0 && expected.length === actual.length && expected.every((v, i) => v === actual[i]);
        }
        case 'dropdown': return sameSelection(answer, correct);
        case 'short-answer': return normalized(correct) !== '' && normalized(answer) === normalized(correct);
        case 'matching': {
            const keys = Object.keys(correct || {});
            return keys.length > 0 && keys.length === Object.keys(answer || {}).length && keys.every(key => sameSelection(answer?.[key], correct[key]));
        }
        case 'mcq-grid': return Boolean(question.rows?.length) && question.rows.every((_, index) => sameSelection(answer?.[index], correct?.[index]));
        default: return false;
    }
}

export function descriptiveAnswerForAI(answer) {
    const word = typeof answer === 'string' ? answer : answer?.wordContent;
    const sheet = answer?.excelData ? spreadsheetToText(answer.excelData) : '';
    return [word ? `Written response:\n${word}` : '', sheet ? `Spreadsheet working:\n${sheet}` : ''].filter(Boolean).join('\n\n');
}

export function failedEvaluation(error, marks) {
    const code = String(error?.code || 'unknown').replace(/^functions\//, '');
    const reasons = {
        'unauthenticated': ['Sign in again, then ask an administrator to retry the saved answer.', false],
        'permission-denied': ['An administrator with permission for this paper must retry this answer.', false],
        'deadline-exceeded': ['Automatic marking took too long. An administrator can retry the pending answer.', true],
        'unavailable': ['The marking service is temporarily unavailable. An administrator can retry the pending answer.', true],
        'resource-exhausted': ['The marking service is busy. Wait a moment, then retry the pending answer.', true],
        'invalid-argument': ['An administrator must check the model answer, marking scheme and maximum marks before retrying.', false],
        'failed-precondition': ['An administrator must check the marking service setup before retrying this answer.', false],
        'evaluation-material-too-large': ['The answer or reference images exceed the marking limit. An administrator should review this answer or reduce the reference material before retrying.', false],
        'evaluation-material-unavailable': ['Some answer or reference material could not be loaded. Check the connection and retry this answer.', true],
        'evaluation-material-unsupported': ['Some answer or reference material could not be read for automatic marking. An administrator should review this answer.', false],
    };
    const [reason, retryable] = reasons[code] || ['Automatic marking did not finish. An administrator can retry the pending answer.', true];
    // Provider errors can contain request content. Store a stable category and
    // safe explanation rather than copying an external exception into results.
    return {mark: null, maxMarks: marks, method: 'ai', status: code === 'evaluation-material-unsupported' ? 'needs-review' : 'failed', text: `Your answer is saved. ${reason}`, error: reason, errorCode: code.slice(0, 80), retryable: typeof error?.details?.retryable === 'boolean' ? error.details.retryable : retryable};
}

// This engine is shared by first marking and approved revaluation. It receives the
// saved question version and nested case-study answers, and never changes either.
export async function evaluateExamAnswers({ questions, answers = {}, examId, strict = false, previousEvaluations = {}, retryPendingOnly = false, onProgress, timeoutMs = EVALUATION_TIMEOUT_MS, batchTimeoutMs = EVALUATION_BATCH_TIMEOUT_MS }) {
    const evaluations = {};
    const jobs = [];
    let score = 0;
    let autoGradedTotalMarks = 0;
    let descriptiveTotalMarks = 0;
    const saveEvaluation = (entry, evaluation) => {
        if (entry.subIndex === null) evaluations[entry.index] = evaluation;
        else {
            if (!evaluations[entry.index]) evaluations[entry.index] = {};
            evaluations[entry.index][entry.subIndex] = evaluation;
        }
    };
    for (const entry of flattenExamQuestions(questions)) {
        const q = entry.question;
        const marks = markValue(q.marks);
        const answer = getQuestionEntry(answers, entry.index, entry.subIndex, q.id);
        if (q.type !== 'descriptive') {
            autoGradedTotalMarks += marks;
            if (gradeObjectiveQuestion(q, answer)) score += marks;
            continue;
        }
        descriptiveTotalMarks += marks;
        const previous = getQuestionEntry(previousEvaluations, entry.index, entry.subIndex, q.id);
        if (retryPendingOnly && getEvaluationMark(previous) !== null) {
            saveEvaluation(entry, previous);
        } else if (hasStarterSpreadsheet(q) && q.starterSpreadsheet.gradingMode === 'cells') {
            const evaluation = gradeStarterSpreadsheet(q, answer);
            saveEvaluation(entry, { ...evaluation, maxMarks: marks, ...(evaluation.needsReview ? { mark: null, status: 'needs-review' } : { status: 'graded' }) });
        } else if (!hasDescriptiveResponse(q, answer) && !hasDrawnAnswer(answer)) {
            saveEvaluation(entry, { text: 'No answer provided.', mark: 0, maxMarks: marks, method: 'unanswered', status: 'graded' });
        } else {
            jobs.push({ entry, q, marks, answer });
        }
    }
    const deadline = Date.now() + batchTimeoutMs;
    let cursor = 0;
    let completed = 0;
    const report = () => onProgress?.({ completed, total: jobs.length });
    report();
    const evaluate = jobs.length ? httpsCallable(functions, 'evaluateDescriptiveAnswer', { timeout: timeoutMs }) : null;
    const worker = async () => {
        while (cursor < jobs.length) {
            const { entry, q, marks, answer } = jobs[cursor++];
            const controller = typeof AbortController === 'function' ? new AbortController() : null;
            try {
                let remaining = deadline - Date.now();
                if (remaining <= 0) throw Object.assign(new Error('Marking time limit reached.'), {code: 'deadline-exceeded'});
                const material = await withEvaluationTimeout(buildEvaluationMaterial(q, answer, {
                    ...(entry.subIndex !== null ? {scenario: questions[entry.index]} : {}),
                    ...(controller ? {signal: controller.signal} : {}),
                }), Math.min(30000, remaining), 'Answer material could not be loaded in time.');
                remaining = deadline - Date.now();
                if (remaining <= 0) throw Object.assign(new Error('Marking time limit reached.'), {code: 'deadline-exceeded'});
                const response = await withEvaluationTimeout(evaluate({
                    ...material,
                    evaluationProtocol: 2,
                    markingScheme: [material.markingScheme || '', hasStarterSpreadsheet(q) ? `Award marks only for student work in these answer cells: ${q.starterSpreadsheet.answerRange}. Other cells are prefilled givens.` : ''].filter(Boolean).join('\n'),
                    marks,
                    ...(examId ? { examId } : {}),
                }), Math.min(timeoutMs, remaining));
                const evaluation = createAIEvaluation(response.data, marks);
                const model = response.data?.model;
                if (typeof model === 'string' && /^[A-Za-z0-9._/-]{1,120}$/.test(model)) evaluation.model = model;
                const requestedModel = response.data?.requestedModel;
                if (typeof requestedModel === 'string' && /^[A-Za-z0-9._/-]{1,120}$/.test(requestedModel)) evaluation.requestedModel = requestedModel;
                saveEvaluation(entry, evaluation);
            } catch (error) {
                saveEvaluation(entry, failedEvaluation(error, marks));
            } finally {
                controller?.abort();
            }
            completed++;
            report();
        }
    };
    // Two in flight avoids sending an entire exam to the AI provider at once.
    await Promise.all(Array.from({ length: Math.min(2, jobs.length) }, worker));
    const { aiScore, pendingCount } = getAttemptScores({ evaluations });
    if (strict && pendingCount) throw new Error(`Revaluation could not confirm ${pendingCount} answer(s). Existing marks have been kept. Please retry or grade these answers manually.`);
    return {
        score: Number(score.toFixed(2)), autoGradedTotalMarks: Number(autoGradedTotalMarks.toFixed(2)),
        descriptiveTotalMarks: Number(descriptiveTotalMarks.toFixed(2)), evaluations, aiGradedScore: aiScore, pendingCount,
    };
}
